import React, { useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import { ImageGrid } from '../ImageCarousel/ImageCarousel';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<Object>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [desktopLightboxOpen, setDesktopLightboxOpen] = useState(false);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants, listingTitle, listing } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      image: img,
    };
  });
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreenState) => {
    return isFullscreenState ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const layoutClasses = classNames(rootClassName || css.root, className);
  const viewAllPhotosLabel = intl.formatMessage(
    { id: 'ListingImageGallery.viewImagesButton' },
    { count: images.length }
  );

  const sharedGalleryProps = {
    items,
    renderItem,
    renderLeftNav,
    renderRightNav,
    ...IMAGE_GALLERY_OPTIONS,
  };

  return (
    <div className={css.galleryRoot}>
      <div className={css.carouselMobile}>
        <ReactImageGallery
          additionalClass={layoutClasses}
          onScreenChange={onScreenChange}
          renderFullscreenButton={renderFullscreenButton}
          {...sharedGalleryProps}
        />
      </div>

      <div className={css.gridDesktop}>
        <ImageGrid
          images={images}
          imageVariants={imageVariants}
          imageAltId="ListingImageGallery.imageAltText"
          sizes="(max-width: 1200px) 50vw, 564px"
          onViewAllClick={() => setDesktopLightboxOpen(true)}
          viewAllLabel={viewAllPhotosLabel}
        />
        {images.length > 1 ? (
          <button
            type="button"
            className={css.openFullscreen}
            onClick={() => setDesktopLightboxOpen(true)}
          >
            <FormattedMessage
              id="ListingImageGallery.viewImagesButton"
              values={{ count: images.length }}
            />
          </button>
        ) : null}
      </div>

      {desktopLightboxOpen ? (
        <div className={css.desktopLightbox} role="dialog" aria-modal="true">
          <Button
            onClick={() => setDesktopLightboxOpen(false)}
            rootClassName={css.close}
            title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
          >
            <span className={css.closeText}>
              <FormattedMessage id="ListingImageGallery.closeModal" />
            </span>
            <IconClose rootClassName={css.closeIcon} />
          </Button>
          <ReactImageGallery
            additionalClass={classNames(layoutClasses, css.desktopLightboxGallery)}
            showFullscreenButton={false}
            {...sharedGalleryProps}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ListingImageGallery;
